/* eslint-disable react-hooks/rules-of-hooks */
import React, { ReactNode, useState, createContext, useContext } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import tsx from "react-syntax-highlighter/dist/esm/languages/prism/tsx";
import typescript from "react-syntax-highlighter/dist/esm/languages/prism/typescript";
import scss from "react-syntax-highlighter/dist/esm/languages/prism/scss";
import bash from "react-syntax-highlighter/dist/esm/languages/prism/bash";
import markdown from "react-syntax-highlighter/dist/esm/languages/prism/markdown";
import json from "react-syntax-highlighter/dist/esm/languages/prism/json";
import python from "react-syntax-highlighter/dist/esm/languages/prism/python";
import javascript from "react-syntax-highlighter/dist/esm/languages/prism/javascript";
import jsx from "react-syntax-highlighter/dist/esm/languages/prism/jsx";
import rangeParser from "parse-numeric-range";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { showSuccessNotification } from "../helpers/notifications";
import useTranslation from "../helpers/useTranslation";
import { MarkdownRendererI18n } from "./MarkdownRenderer.i18n";
import colors from "../utils/colors";
import getThemeColors from "../utils/getColorTheme";
import { useTheme } from "../contexts/ThemeContext";
import uploadVideo from "../assets/videos/FAM upload feature.mov";

SyntaxHighlighter.registerLanguage("tsx", tsx);
SyntaxHighlighter.registerLanguage("typescript", typescript);
SyntaxHighlighter.registerLanguage("scss", scss);
SyntaxHighlighter.registerLanguage("bash", bash);
SyntaxHighlighter.registerLanguage("markdown", markdown);
SyntaxHighlighter.registerLanguage("json", json);
SyntaxHighlighter.registerLanguage("python", python);
SyntaxHighlighter.registerLanguage("javascript", javascript);
SyntaxHighlighter.registerLanguage("jsx", jsx);

const syntaxTheme = oneDark;
const RequestContext = createContext(false);
const ThemeColorContext = createContext("#16140E");

const MarkdownCodeRenderer: object = {
  code({
    node,
    inline,
    className,
    ...props
  }: {
    node: { data: { meta: string } };
    inline: boolean;
    className: string;
  } & Record<string, unknown>): ReactNode {
    const [isCodeCopied, setIsCodeCopied] = useState(false);
    const { t } = useTranslation(MarkdownRendererI18n);
    const match = /language-(\w+)/.exec(className || "");
    const hasMeta = node?.data?.meta;
    const isRequest = useContext(RequestContext);

    const applyHighlights: object = (applyHighlights: number) => {
      if (hasMeta) {
        const RE = /{([\d,-]+)}/;
        const metadata = node.data.meta?.replace(/\s/g, "");
        const strlineNumbers = RE?.test(metadata)
          ? RE?.exec(metadata)![1]
          : "0";
        const highlightLines = rangeParser(strlineNumbers);
        if (highlightLines?.includes(applyHighlights)) {
          return { className: "highlight" };
        }
      }
      return {};
    };

    const children =
      typeof props.children === "string" || Array.isArray(props.children)
        ? props.children
        : "";

    const handleCopyCode = code => {
      navigator.clipboard.writeText(code);
      showSuccessNotification(t("notifications.copy-code"));
      setIsCodeCopied(true);
      setTimeout(() => {
        setIsCodeCopied(false);
      }, 2500);
    };

    return match ? (
      <div className="bg-body">
        <div className="d-flex align-items-center justify-content-between pt-1 px-2">
          <span className="mt-1">{match[1]}</span>
          <button
            className="btn btn-sm btn-outline-secondary align-content-center pt-2"
            onClick={() => handleCopyCode(children)}
            disabled={isCodeCopied}
            aria-label={isCodeCopied ? "Code copied" : "Copy code"}
          >
            {isCodeCopied ? (
              <>
                {t("button.copied")} <i className="bx bx-check"></i>
              </>
            ) : (
              <>
                {t("button.copy-code")} <i className="bx bx-copy"></i>
              </>
            )}
          </button>
        </div>
        <SyntaxHighlighter
          style={syntaxTheme}
          language={match[1]}
          PreTag="div"
          className="codeStyle"
          showLineNumbers={false}
          wrapLines={!!hasMeta}
          useInlineStyles={true}
          lineProps={applyHighlights}
          {...props}
        >
          {children}
        </SyntaxHighlighter>
      </div>
    ) : (
      <code
        className={className}
        style={{
          color: isRequest ? colors.theme : colors.backgroundMain,
          fontSize: "16px",
        }}
        {...props}
      />
    );
  },

  // 👇 Nuevo renderizador de links
  a({ href, children, ...props }) {
    const isRequest = useContext(RequestContext);
    const themeColor = useContext(ThemeColorContext);
    const navigate = useNavigate();
    
    // Check if it's an internal link (starts with /)
    const isInternalLink = href && href.startsWith('/');
    
    if (isInternalLink) {
      // Handle internal navigation
      const handleInternalClick = (e) => {
        e.preventDefault();
        
        // Parse URL and search params
        const url = new URL(href, window.location.origin);
        const searchParams = new URLSearchParams(url.search);
        
        // If it's the signin page with signup=true, navigate with appropriate state
        if (url.pathname === '/signin' && searchParams.get('signup') === 'true') {
          navigate('/signin', { state: { isSingup: true } });
        } else {
          navigate(href);
        }
      };
      
      return (
        <a 
          href={href}
          onClick={handleInternalClick}
          style={{ 
            color: isRequest ? colors.lightYellow : themeColor,
            cursor: 'pointer'
          }}
          {...props}
        >
          {children}
        </a>
      );
    }
    
    // External links - open in new tab as before
    return (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer" 
        style={{ 
          color: isRequest ? colors.lightYellow : themeColor
        }}
        {...props}
      >
        {children}
      </a>
    );
  },

  // 👇 Custom list item renderer with tab spacing
  li({ children, ...props }) {
    return (
      <li 
        style={{ 
          marginLeft: '24px', // Add tab-like spacing (approximately 1 tab = 24px)
          marginBottom: '4px' // Small spacing between list items
        }}
        {...props}
      >
        {children}
      </li>
    );
  },
};

type Props = {
  content: string;
  className?: string;
  "data-is-request"?: boolean;
};

const MarkdownRenderer: React.FC<Props> = ({
  content,
  className,
  "data-is-request": isRequest = false,
}) => {
  const { addTheme } = useTheme();
  const { background } = getThemeColors(addTheme);

  const themeColor =
    addTheme === "dark"
      ? "#F9F5ED"
      : addTheme === "eclipse"
      ? "#16140E"
      : "#16140E";

  let textMessage = content?.includes("[###]")
    ? content.split("[###]")[1]
    : content?.startsWith("data:audio/")
    ? "..."
    : content;

  // Handle video tags - replace with JSX component before rendering
  const hasVideo = textMessage?.includes("[VIDEO:FAM_UPLOAD]");
  if (hasVideo) {
    const parts = textMessage.split("[VIDEO:FAM_UPLOAD]");
    return (
      <RequestContext.Provider value={isRequest}>
        <ThemeColorContext.Provider value={themeColor}>
          <div className="d-flex flex-column justify-content-center w-100">
            {parts[0] && (
              <ReactMarkdown
                className={className}
                components={MarkdownCodeRenderer}
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {parts[0]}
              </ReactMarkdown>
            )}
            <div style={{ marginTop: "12px", marginBottom: "12px" }}>
              <video 
                src={uploadVideo} 
                controls 
                style={{ 
                  width: "100%", 
                  maxWidth: "100%", 
                  borderRadius: "8px",
                  display: "block"
                }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
            {parts[1] && (
              <ReactMarkdown
                className={className}
                components={MarkdownCodeRenderer}
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {parts[1]}
              </ReactMarkdown>
            )}
          </div>
        </ThemeColorContext.Provider>
      </RequestContext.Provider>
    );
  }

  return (
    <RequestContext.Provider value={isRequest}>
      <ThemeColorContext.Provider value={themeColor}>
        <div className="d-flex flex-column justify-content-center w-100">
          <ReactMarkdown
            className={className}
            components={MarkdownCodeRenderer}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
          >
            {textMessage}
          </ReactMarkdown>
        </div>
      </ThemeColorContext.Provider>
    </RequestContext.Provider>
  );
};

export default MarkdownRenderer;
